import { Box, Flex, Heading, HStack, Stack, Text } from "@chakra-ui/react";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { borderRadius, borderStyle } from "../../../../utils/styles/styles";
import LabelComponent from "../../../../components/atom/Label/LabelComponent";
import FileUpload from "../../../../components/molecule/FileUpload/FileUpload";
import { BsCheck2Circle } from "react-icons/bs";
import { successColor } from "../../../../utils/colors";
import { tableStyles } from "../../../../utils/styles/tableStyles";
import FileDownload from "../../../../components/molecule/FileDownload/FileDownload";
import AlertComponent from "../../../../components/atom/Alert/AlertComponent";
import Pagination from "../../../../components/organism/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "../../../../utils/constants";
import { TableConfig } from "../../../../types/Components/Table/TableConfig";
import { PaginationConfig } from "../../../../types/Components/Table/PaginationConfig";
import LoadingButton from "../../../../components/atom/Button/LoadingButton/LoadingButton";

interface UploadCompletedTemplateFormProps {
  partnerName: string;
  uploadDate?: string;
  uploadedFile: File | null | undefined;
  setUploadedFile: Dispatch<SetStateAction<File | null | undefined>>;
  isUploadExecuted: boolean;
  tableConfig: TableConfig;
  paginationConfig: PaginationConfig;
  handleUploadFile: () => Promise<void>;
}

const UploadCompletedTemplateForm: FC<UploadCompletedTemplateFormProps> = ({
  partnerName,
  uploadDate = "",
  uploadedFile,
  setUploadedFile,
  isUploadExecuted,
  tableConfig,
  paginationConfig,
  handleUploadFile,
}) => {
  const { tableName = "Lista de Itens", dataTable } = tableConfig;
  const {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
  } = paginationConfig;

  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
  };

  const handleUploadClick = async () => {
    setIsUploading(true);
    try {
      await handleUploadFile();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Stack spacing={8}>
      <Box>
        <LabelComponent
          title="Parceiro"
          fontWeight="500"
          fontSize="md"
          isRequired
        />
        <Text>{partnerName}</Text>
      </Box>
      {uploadDate && (
        <Box>
          <LabelComponent
            title="Periodo de carregamento"
            fontWeight="500"
            fontSize="md"
          />
          <Text>{uploadDate}</Text>
        </Box>
      )}
      <Flex direction="column">
        <Stack
          spacing={4}
          border={borderStyle}
          borderRadius={borderRadius}
          background="gray.100"
          p={6}
          borderBottomRadius={uploadedFile ? "0" : borderRadius}
        >
          <Flex justifyContent="space-between">
            <Heading as="h3" fontSize="2xl">
              Ficheiro excel
            </Heading>
            <FileDownload
              file={uploadedFile}
              buttonName="Descarregar template excel"
            />
          </Flex>
          <AlertComponent
            title="Os dados verificados e que estejam conformes são automaticamente carregados no sistema."
            status="warning"
            description="Uma vez que os dados conformes sejam verificados, só será possível substituí-los se verificar um novo ficheiro com dados conformes."
            borderColor="orange.500"
          />
          <HStack gap="4">
            <FileUpload
              file={uploadedFile}
              setFile={setUploadedFile}
              onFileChange={handleFileChange}
              validExtensions={["xls", "xlsx"]}
              uploadLimit={10}
              buttonName="Carregar (10 mb)"
              loadingFile={false}
            />
            <LoadingButton
              isLoading={isUploading}
              aria-label="Verificar e carregar dados no sistema"
              variant="primary"
              onClick={handleUploadClick}
              isButtonDisabled={isUploading}
            >
              Verificar e carregar dados no sistema
            </LoadingButton>
          </HStack>
        </Stack>
        {isUploadExecuted && (
          <Stack
            gap={4}
            border={borderStyle}
            borderRadius={borderRadius}
            background="gray.50"
            p={6}
            borderTopRadius="0"
            borderTop="0"
          >
            <Heading as="h3" fontSize="2xl">
              Resultado
            </Heading>

            {dataTable.length > 0 ? (
              <>
                <Box>
                  <AlertComponent
                    title="Atenção! Foram detectados erros nos valores preenchidos na tabela."
                    status="warning"
                    description={
                      <>
                        Os valores assinalados com "erro"
                        <strong>NÃO FORAM</strong> carregados no sistema. Para
                        efetuar o carregamento destes valores no sistema, deverá
                        recomeçar este processo. Descarregue e preencha um novo
                        template e evite os erros detetados abaixo. Carregue
                        novamente o template sem erros.
                      </>
                    }
                    borderColor="orange.500"
                  />
                </Box>
                <Stack gap="2.5">
                  <Flex
                    direction="row"
                    gap="4"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Heading as="h4" fontSize="xl" my="10px">
                      {tableName}
                    </Heading>
                    <Pagination
                      totalResults={dataTable.length}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      totalPages={totalPages}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      handlePageChange={handlePageChange}
                      setPageSize={setPageSize}
                    />
                  </Flex>
                  <Flex direction="column" gap="2.5">
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      sx={tableStyles}
                    >
                      {renderContent()}
                    </Box>
                  </Flex>
                </Stack>
              </>
            ) : (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                borderRadius={borderRadius}
                border={borderStyle}
                padding={12}
                backgroundColor="gray.100"
              >
                <Heading as="h2" fontSize="3xl">
                  Excel sem erros
                </Heading>
                <BsCheck2Circle color={successColor} size="148" />
              </Flex>
            )}
          </Stack>
        )}
      </Flex>
    </Stack>
  );
};

export default UploadCompletedTemplateForm;
